<template>
  <el-table class="table" :data="channelsWithStatus" stripe>
    <el-table-column :label="title">
      <template v-slot="{ row }">
        <el-tooltip
          :content="row.message"
          :disabled="!row.message"
          :style="{ cursor: row.disabled ? 'not-allowed' : 'pointer' }"
        >
          <checkbox
            :label="row.field"
            :title="row.title"
            :icon="row.icon"
            v-model="value[row.field]"
            :disabled="row.disabled"
            @input="val => toggleChannel(row.field, val)"
            :class="{ warning: row.warning }"
            :data-qa="`channel-${row.field}-checkbox`"
          />
        </el-tooltip>
      </template>
    </el-table-column>

    <el-table-column
      width="40"
      v-for="platform in platforms"
      :key="platform.code"
    >
      <template #header>
        <el-tooltip :content="platform.name">
          <icon class="icon" :glyph="platform.icon" />
        </el-tooltip>
      </template>
      <template v-slot="{ row }">
        <el-tooltip
          v-if="row.platforms[platform.code]"
          :content="row.platforms[platform.code].message"
          :disabled="!row.platforms[platform.code].message"
          :style="{
            cursor: row.platforms[platform.code].disabled
              ? 'not-allowed'
              : 'pointer'
          }"
        >
          <checkbox
            v-model="value[row.platforms[platform.code].field]"
            :disabled="row.platforms[platform.code].disabled"
            @input="val => toggleChannelPlatform(row.field, val)"
            :class="{ warning: row.warning }"
            class="platform-checkbox"
            :data-qa="`platform-${row.field}-${platform.code}-checkbox`"
          />
        </el-tooltip>
      </template>
    </el-table-column>

    <el-table-column width="4" />
  </el-table>
</template>

<script>
import Checkbox from '@/common/components/form/Checkbox'
import { EventBus } from '@/core/EventBus'

export default {
  name: 'ChannelsSelectGroup',
  components: { Checkbox },

  props: {
    value: {
      required: true
    },
    channels: {
      type: Array,
      required: true
    },
    platforms: {
      type: Array,
      default: () => {
        return []
      }
    },
    title: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      consentedChannels: null
    }
  },

  mounted() {
    EventBus.$on('set-preference-available-channels', channels => {
      this.consentedChannels = channels
    })
  },

  destroyed() {
    EventBus.$off('set-preference-available-channels')
  },

  watch: {
    'value.consent_preference': {
      handler(val) {
        if (val === 0) {
          this.consentedChannels = null
        }
      }
    }
  },

  computed: {
    channelsWithStatus() {
      return this.channels.map(channel => {
        channel.consented =
          !this.consentedChannels ||
          !channel.consent ||
          this.consentedChannels.includes(channel.consent)

        channel.disabled =
          (!channel.consented || !channel.configured) &&
          !this.value[channel.field]

        channel.warning =
          (!channel.consented || !channel.configured) &&
          this.value[channel.field]

        if (!channel.configured) {
          channel.message = this.$t(
            'CampaignForm_ChannelsSelect_Message_ChannelNotEnabled',
            { channel: channel.title }
          )
        } else if (!channel.consented) {
          channel.message = this.$t(
            'CampaignForm_ChannelsSelect_Message_ChannelNotConsented',
            { channel: channel.title }
          )
        } else {
          channel.message = null
        }

        channel.platforms = channel.platforms || {}
        Object.keys(channel.platforms).forEach(platform => {
          let channelPlatform = channel.platforms[platform]

          channelPlatform.disabled =
            (!channel.consented ||
              !channel.configured ||
              !channelPlatform.configured) &&
            !this.value[channelPlatform.field]

          channelPlatform.warning =
            (!channel.consented ||
              !channel.configured ||
              !channelPlatform.configured) &&
            this.value[channelPlatform.field]

          if (channel.message) {
            channelPlatform.message = channel.message
          } else if (!channelPlatform.configured) {
            channelPlatform.message = this.$t(
              'CampaignForm_ChannelsSelect_Message_ChannelPlatformNotEnabled',
              {
                channel: channel.title,
                platform: this.platforms.find(item => item.code === platform)
                  .name
              }
            )
          } else {
            channelPlatform.message = null
          }
        })
        return channel
      })
    }
  },

  methods: {
    toggleChannel(channel, status) {
      let item = this.channels.find(item => {
        return item.field === channel
      })

      if (item.platforms.ios && !item.platforms.ios.disabled) {
        this.$set(this.value, item.platforms.ios.field, status)
      }

      if (item.platforms.android && !item.platforms.android.disabled) {
        this.$set(this.value, item.platforms.android.field, status)
      }

      if (item.platforms.web && !item.platforms.web.disabled) {
        this.$set(this.value, item.platforms.web.field, status)
      }
    },

    toggleChannelPlatform(channel, status) {
      let item = this.channels.find(item => {
        return item.field === channel
      })

      if (status) {
        this.$set(this.value, channel, 1)
      } else {
        if (
          (!item.platforms.ios || !this.value[item.platforms.ios.field]) &&
          (!item.platforms.android ||
            !this.value[item.platforms.android.field]) &&
          (!item.platforms.web || !this.value[item.platforms.web.field])
        ) {
          this.$set(this.value, channel, 0)
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.table {
  border: 1px solid #dbdee7;
  border-radius: 4px;
}

.icon {
  color: #5c6374;
  font-size: 20px;
  padding-top: 3px;
}

/deep/ th {
  height: 55px;
}

/deep/ td {
  height: 55px;
}

/deep/ th:first-child {
  padding-left: 10px;
}

/deep/ th:last-child {
  padding-right: 10px;
}

/deep/ td:first-child {
  padding-left: 10px;
}

/deep/ td:last-child {
  padding-right: 10px;
}

/deep/ .el-checkbox.is-disabled .content-ct {
  opacity: 0.5;
}

/deep/ .platform-checkbox {
  width: 20px;
}

/deep/ .warning .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: red !important;
  border-color: red !important;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/setup/ChannelsSelectGroup.vue